import React, { useMemo, useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  Box,
  Text,
  HStack,
  Checkbox,
  Select,
  useToast,
} from "@chakra-ui/react";
import { fetchOrders, assignOrderItem } from "../../services/clinicComplianceService";
import { createTask } from "../../services/messagingService";
import ColleaguePicker from "./ColleaguePicker";
import { emitWorkItemsChanged } from "../../utils/workItemsBus";
import { messagePlainText } from "../../utils/messageHtml";
import { buildMessageTaskPrefill } from "../../utils/taskPrefill";
import { PRIORITIES, PRIORITY_META, DEFAULT_PRIORITY, normalizePriority } from "../../utils/taskPriority";
import PriorityIcon from "./PriorityIcon";

function TaskTab({
  currentUser,
  schema,
  sourceChannelId,
  sourceMessage,
  initialTitle,
  initialDescription,
  taskSource,
  onTaskCreated,
  onDone,
}) {
  // From a chat message: a one-line title, and the whole message as written as
  // the description when it doesn't fit in that title (it used to be cut at 120).
  const fromMessage = initialTitle == null ? buildMessageTaskPrefill(messagePlainText(sourceMessage)) : null;
  const [title, setTitle] = useState(() => initialTitle ?? fromMessage.title);
  const [description, setDescription] = useState(() => initialDescription || fromMessage?.description || "");
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState(DEFAULT_PRIORITY);
  const [assignee, setAssignee] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!title.trim() || !assignee) return;
    setSubmitting(true);
    try {
      const res = await createTask({
        ...(taskSource || {}),
        title: title.trim(),
        description: description.trim() || null,
        assigned_to: assignee.id,
        assigned_by: currentUser.id,
        due_date: dueDate || null,
        priority,
        source_channel_id: sourceChannelId || null,
        source_message_id: sourceMessage?.id || null,
      });
      onTaskCreated?.(res?.task);
      emitWorkItemsChanged({ kind: "task" });
      toast({ title: "Task assigned", status: "success", duration: 2500 });
      onDone();
    } catch (err) {
      toast({ title: "Couldn't assign task", description: err.message, status: "error", duration: 4000 });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <VStack align="stretch">
      <FormControl mb={3}>
        <FormLabel>Title</FormLabel>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </FormControl>
      <FormControl mb={3}>
        <FormLabel>Description (optional)</FormLabel>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
      </FormControl>
      <FormControl mb={3}>
        <FormLabel>Due date (optional)</FormLabel>
        <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
      </FormControl>
      <FormControl mb={3}>
        <FormLabel>Priority</FormLabel>
        <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>{PRIORITY_META[p].label}</option>
          ))}
        </Select>
      </FormControl>
      <ColleaguePicker label="Assign to" picked={assignee} onPick={setAssignee} schema={schema} />
      <Button colorScheme="blue" isLoading={submitting} isDisabled={!title.trim() || !assignee} onClick={handleSubmit}>
        Assign task
      </Button>
    </VStack>
  );
}

function formatVisitDate(value) {
  if (!value) return "";
  const [y, m, d] = value.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function OrderTab({ currentUser, schema, onDone }) {
  const [patients, setPatients] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(() => new Map()); // id -> item
  const [assignee, setAssignee] = useState(null);
  // "" keeps each order's own priority (a reassign shouldn't reset a High one).
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const { patients: rows } = await fetchOrders({ schema });
        setPatients(rows || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [schema]);

  // Every open order (no cap — the list scrolls); matches patient name or order text.
  const openItems = useMemo(() => {
    const norm = filter.trim().toLowerCase();
    const items = [];
    for (const patient of patients) {
      const patientMatch = (patient.patient_name || "").toLowerCase().includes(norm);
      for (const item of patient.items || []) {
        if (item.status === "completed") continue;
        if (norm && !patientMatch && !(item.description || "").toLowerCase().includes(norm)) continue;
        items.push({ ...item, patient_name: patient.patient_name });
      }
    }
    return items;
  }, [patients, filter]);

  const toggle = (item) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });

  const allShownSelected = openItems.length > 0 && openItems.every((i) => selected.has(i.id));
  const someShownSelected = openItems.some((i) => selected.has(i.id));
  const toggleAllShown = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allShownSelected) openItems.forEach((i) => next.delete(i.id));
      else openItems.forEach((i) => next.set(i.id, i));
      return next;
    });

  const handleSubmit = async () => {
    if (selected.size === 0 || !assignee) return;
    setSubmitting(true);
    const items = [...selected.values()];
    const results = await Promise.allSettled(
      items.map((item) => assignOrderItem(item.id, assignee.id, {
        assignedBy: currentUser.id, schema, ...(priority ? { priority } : {}),
      })),
    );
    setSubmitting(false);
    const failed = items.filter((_, i) => results[i].status === "rejected");
    const who = assignee.name || assignee.email;
    if (failed.length < items.length) emitWorkItemsChanged({ kind: "order" });
    if (failed.length === 0) {
      toast({
        title: `${items.length} order${items.length === 1 ? "" : "s"} assigned to ${who}`,
        status: "success",
        duration: 2500,
      });
      onDone();
      return;
    }
    // Keep only the failures selected so a retry doesn't re-assign the rest.
    setSelected(new Map(failed.map((item) => [item.id, item])));
    toast({
      title: `${items.length - failed.length} of ${items.length} orders assigned`,
      description: `${failed.length} failed — still selected so you can retry.`,
      status: "warning",
      duration: 5000,
    });
  };

  if (loading) return <Text color="gray.500">Loading orders...</Text>;

  return (
    <VStack align="stretch">
      <FormControl mb={2}>
        <FormLabel>Find open orders</FormLabel>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Patient name or order..." />
      </FormControl>
      <HStack justify="space-between" px={1}>
        <Checkbox
          isChecked={allShownSelected}
          isIndeterminate={someShownSelected && !allShownSelected}
          isDisabled={openItems.length === 0}
          onChange={toggleAllShown}
          size="sm"
        >
          <Text fontSize="sm">Select all shown ({openItems.length})</Text>
        </Checkbox>
        {selected.size > 0 && (
          <HStack spacing={2}>
            <Text fontSize="sm" fontWeight="semibold" color="blue.600">
              {selected.size} selected
            </Text>
            <Button size="xs" variant="link" onClick={() => setSelected(new Map())}>
              clear
            </Button>
          </HStack>
        )}
      </HStack>
      <VStack
        align="stretch"
        spacing={0}
        mb={3}
        maxH="280px"
        overflowY="auto"
        overscrollBehavior="contain"
        borderWidth="1px"
        borderRadius="md"
        data-testid="order-list"
      >
        {openItems.length === 0 && (
          <Text fontSize="sm" color="gray.400" p={2}>
            No matching open orders
          </Text>
        )}
        {openItems.map((item) => {
          const isSelected = selected.has(item.id);
          return (
            <Box
              key={item.id}
              px={2}
              py={1.5}
              bg={isSelected ? "blue.50" : "transparent"}
              _hover={{ bg: isSelected ? "blue.50" : "gray.50" }}
              borderBottomWidth="1px"
              borderColor="gray.100"
            >
              <Checkbox isChecked={isSelected} onChange={() => toggle(item)} size="sm" w="100%" alignItems="flex-start">
                <Text fontSize="sm">
                  <PriorityIcon priority={normalizePriority(item.priority)} />{" "}
                  <Text as="span" fontWeight="semibold">
                    {item.patient_name}
                  </Text>{" "}
                  — {item.description}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {[
                    item.scheduled_date && `Visit ${formatVisitDate(item.scheduled_date)}`,
                    item.assigned_to ? `Currently: ${item.assigned_to}` : "Unassigned",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              </Checkbox>
            </Box>
          );
        })}
      </VStack>
      <FormControl mb={3}>
        <FormLabel>Priority</FormLabel>
        <Select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="order-priority">
          <option value="">Keep each order's priority</option>
          {PRIORITIES.map((p) => (
            <option key={p} value={p}>{PRIORITY_META[p].label}</option>
          ))}
        </Select>
      </FormControl>
      <ColleaguePicker label="Assign to" picked={assignee} onPick={setAssignee} schema={schema} />
      <Button colorScheme="blue" isLoading={submitting} isDisabled={selected.size === 0 || !assignee} onClick={handleSubmit}>
        {selected.size > 1 ? `Assign ${selected.size} orders` : "Assign order"}
      </Button>
    </VStack>
  );
}

export default function AssignModal({
  isOpen,
  onClose,
  currentUser,
  sourceChannelId,
  sourceMessage,
  defaultTab = 0,
  onAssigned,
  // Note "Assign task" flow: prefilled form, source-doc fields sent with the
  // task, and the created task handed back so the page can link the text.
  initialTitle,
  initialDescription,
  taskSource,
  onTaskCreated,
  // Only the task form (no Order tab), e.g. assigning a memory issue.
  taskOnly = false,
}) {
  const schema = currentUser?.schema;
  const handleDone = () => {
    onAssigned?.();
    onClose();
  };
  const taskTab = (
    <TaskTab
      currentUser={currentUser}
      schema={schema}
      sourceChannelId={sourceChannelId}
      sourceMessage={sourceMessage}
      initialTitle={initialTitle}
      initialDescription={initialDescription}
      taskSource={taskSource}
      onTaskCreated={onTaskCreated}
      onDone={handleDone}
    />
  );
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{taskOnly ? "Assign Task" : "Assign Task or Order"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {taskOnly ? (
            taskTab
          ) : (
            <Tabs defaultIndex={defaultTab}>
              <TabList>
                <Tab>Task</Tab>
                <Tab>Order</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>{taskTab}</TabPanel>
                <TabPanel px={0}>
                  <OrderTab currentUser={currentUser} schema={schema} onDone={handleDone} />
                </TabPanel>
              </TabPanels>
            </Tabs>
          )}
        </ModalBody>
        <ModalFooter />
      </ModalContent>
    </Modal>
  );
}
